/**
 * PortalArayuzleriPage.jsx — Sistem Ayarları › Portal Arayüzleri (backlog #411 salt okunur, #417 DÜZENLENEBİLİR, 2026-10-14;
 * tasarım #409 D3 / D11)
 * Route: /Management/SistemAyarlari/PortalArayuzleri   İzin: portal.arayuz.manage (kök + Admin)
 *
 * Firma portalının SEKİZ arayüz seti — firmanın seçili fuardaki rolü (Katılımcı / Dekoratör / Organizatör / Taşeron) ×
 * uyruğu (Yerli / Yabancı) — ve her setin on üç modüldeki TAVANI (NONE / READ / WRITE / MANAGE). Bir girişin etkin seviyesi
 * = min(bu tavan, girişin rollerinin en iyisi, firma kısıtı): tavan, hiçbir rolün aşamayacağı sınırdır.
 *
 * #417: tavanlar artık VERİ (portal_arayuz_modul, GET /portal-rbac/arayuzler) ve bu sayfadan düzenlenir. Kaydetmek ÖNCE etki
 * önizlemesi gösterir ("12 firma, 37 kullanıcı etkilenir"): çalışan fuarlardaki hangi firma ve girişin ETKİN seviyesi değişir;
 * yalnızca onaydan sonra yazılır (PUT ?onay=true). Her değişen hücre Yetki Yönetimi › Geçmiş'e bir satır düşer.
 *
 * WAS (#411 → #417): salt okunur; veri GET /portal/matris'ten (kodun matrisi), altta "WRITE / MANAGE için gereken portal yetkisi"
 * tablosu. O tablo kalktı: yetki 7-15 artık seviyeyi belirlemiyor, rol belirliyor (Portal Rolleri sayfası).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Table, Alert, Spinner, Badge, Button, Input,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { SEVIYE } from "../../../services/portalService";
import { getArayuzler, putArayuzler } from "../../../services/portalRbacService";

const SEVIYE_RENK = { NONE: "light", READ: "info", WRITE: "primary", MANAGE: "success" };
const SEVIYELER = [SEVIYE.NONE, SEVIYE.READ, SEVIYE.WRITE, SEVIYE.MANAGE];

function SeviyeChip({ seviye }) {
  const { t } = useTranslation("ayarlar");
  return (
    <Badge color={SEVIYE_RENK[seviye] || "secondary"} className={seviye === "NONE" ? "text-muted fw-normal" : ""}>
      {t(`portalArayuzleri.seviye.${seviye}`)}
    </Badge>
  );
}

const hucre = (set, modul) => `${set.kod}|${modul}`;

export default function PortalArayuzleriPage() {
  const { t, i18n } = useTranslation("ayarlar");
  const { t: tp } = useTranslation("portal");
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [taslak, setTaslak] = useState({});          // { "SET_KODU|MODUL": SEVIYE } yalnızca değişenler
  const [onizleme, setOnizleme] = useState(null);    // etki önizlemesi cevabı (modal açık)
  const [calisiyor, setCalisiyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);
  const [bilgi, setBilgi] = useState(null);

  useEffect(() => { document.title = `${t("portalArayuzleri.baslik")} | FDS`; }, [t, i18n.language]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect); yazmadan sonra yeniden okuma `yenile` sayacıyla.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const data = await getArayuzler();
        if (!ignore) { setVeri(data); setHata(null); setTaslak({}); }
      } catch (e) {
        if (!ignore) setHata(e.message || t("portalArayuzleri.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yenile]);

  const setler = useMemo(() => veri?.arayuzler ?? [], [veri]);
  const moduller = useMemo(() => veri?.moduller ?? [], [veri]);
  const setAdi = (s) => `${t(s.yabanci ? "portalArayuzleri.yabanci" : "portalArayuzleri.yerli")} ${tp(`arayuz.${s.rol}`)}`;

  const degisiklikler = useMemo(() => {
    const out = [];
    for (const s of setler) {
      for (const m of moduller) {
        const yeni = taslak[hucre(s, m)];
        if (yeni && yeni !== s.moduller[m]) out.push({ rol: s.rol, yabanci: s.yabanci, modul: m, seviye: yeni });
      }
    }
    return out;
  }, [setler, moduller, taslak]);

  const degistir = (s, m, seviye) => {
    setBilgi(null);
    setTaslak((x) => {
      const k = hucre(s, m);
      const y = { ...x };
      if (seviye === s.moduller[m]) delete y[k]; else y[k] = seviye;
      return y;
    });
  };

  // 1. adım: önizleme (yazmaz). Backend onay verilmedikçe hiçbir şey yazmaz.
  const onizle = async () => {
    setCalisiyor(true);
    setIslemHatasi(null);
    try {
      setOnizleme(await putArayuzler(degisiklikler, { dryRun: true }));
    } catch (e) {
      setIslemHatasi(e.message || t("portalArayuzleri.kaydedilemedi"));
    } finally {
      setCalisiyor(false);
    }
  };

  // 2. adım: onay (yazar).
  const onayla = async () => {
    setCalisiyor(true);
    setIslemHatasi(null);
    try {
      const sonuc = await putArayuzler(degisiklikler, { onay: true });
      setOnizleme(null);
      setBilgi(t("portalArayuzleri.kaydedildi", { n: sonuc.degisenHucre ?? degisiklikler.length }));
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalArayuzleri.kaydedilemedi"));
    } finally {
      setCalisiyor(false);
    }
  };

  const etki = onizleme?.etki;
  const hucreBaslik = (s, m) => (s.kodTabani?.[m] && s.kodTabani[m] !== s.moduller[m]
    ? t("portalArayuzleri.kodDegeri", { seviye: t(`portalArayuzleri.seviye.${s.kodTabani[m]}`) })
    : undefined);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("portalArayuzleri.baslik")} pageTitle={t("portalArayuzleri.ustBaslik")} />
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="mb-1">{t("portalArayuzleri.baslik")}</h5>
              <div className="text-muted small">{t("portalArayuzleri.aciklama")}</div>
            </div>
            <Button color="light" size="sm" onClick={() => { setVeri(null); setYenile((x) => x + 1); }}>
              <RefreshCw size={14} className="me-1" />{t("portalArayuzleri.yenile")}
            </Button>
          </CardHeader>
          <CardBody>
            {hata && <Alert color="danger">{hata}</Alert>}
            {bilgi && <Alert color="success" toggle={() => setBilgi(null)} data-testid="portal-arayuz-bilgi">{bilgi}</Alert>}
            {islemHatasi && !onizleme && <Alert color="danger" className="small">{islemHatasi}</Alert>}
            {!veri && !hata && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}
            {veri && (
              <>
                <Alert color="info" className="small" data-testid="portal-arayuz-kaynak">{t("portalArayuzleri.kaynakTablo")}</Alert>

                <div className="d-flex flex-wrap gap-3 mb-3 small" data-testid="portal-arayuz-lejant">
                  {SEVIYELER.map((s) => (
                    <span key={s}><SeviyeChip seviye={s} /> <span className="text-muted">{t(`portalArayuzleri.seviyeAciklama.${s}`)}</span></span>
                  ))}
                </div>

                <div className="table-responsive">
                  <Table bordered size="sm" className="align-middle text-center mb-0" data-testid="portal-arayuz-matrisi">
                    <thead className="table-light">
                      <tr>
                        <th className="text-start">{t("portalArayuzleri.set")}</th>
                        {moduller.map((m) => <th key={m} className="small">{t(`portalArayuzleri.modul.${m}`)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {setler.map((s) => (
                        <tr key={s.kod} data-set={s.kod}>
                          <th className="text-start fw-semibold text-nowrap">{setAdi(s)}</th>
                          {moduller.map((m) => {
                            const kayitli = s.moduller[m] ?? "NONE";
                            const deger = taslak[hucre(s, m)] ?? kayitli;
                            const degisti = deger !== kayitli;
                            const kodlaFarkli = s.kodTabani?.[m] && s.kodTabani[m] !== kayitli;
                            return (
                              <td
                                key={m} data-modul={m} data-seviye={kayitli} data-degisti={degisti ? "true" : "false"}
                                className={degisti ? "table-warning" : undefined} title={hucreBaslik(s, m)}
                              >
                                <Input
                                  type="select" bsSize="sm" value={deger} data-testid={`arayuz-${s.kod}-${m}`}
                                  className={kodlaFarkli ? "border-primary" : undefined}
                                  onChange={(e) => degistir(s, m, e.target.value)}
                                >
                                  {SEVIYELER.map((x) => <option key={x} value={x}>{t(`portalArayuzleri.seviye.${x}`)}</option>)}
                                </Input>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="text-muted small mt-2">{t("portalArayuzleri.kodIpucu")}</div>

                <div className="d-flex align-items-center gap-2 mt-3">
                  <Button color="primary" disabled={degisiklikler.length === 0 || calisiyor} onClick={onizle} data-testid="portal-arayuz-kaydet">
                    {calisiyor && !onizleme ? <Spinner size="sm" /> : t("portalArayuzleri.kaydet")}
                  </Button>
                  <Button color="light" disabled={degisiklikler.length === 0 || calisiyor} onClick={() => setTaslak({})}>
                    {t("portalArayuzleri.vazgec")}
                  </Button>
                  {degisiklikler.length > 0 && (
                    <Badge color="warning" pill data-testid="portal-arayuz-degisiklik">{t("portalArayuzleri.degisiklikSayisi", { n: degisiklikler.length })}</Badge>
                  )}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* Etki önizlemesi: onaydan önce */}
        <Modal isOpen={Boolean(onizleme)} toggle={() => setOnizleme(null)} size="lg" scrollable>
          <ModalHeader toggle={() => setOnizleme(null)}>{t("portalArayuzleri.etkiBaslik")}</ModalHeader>
          <ModalBody data-testid="portal-arayuz-etki">
            {islemHatasi && <Alert color="danger" className="small">{islemHatasi}</Alert>}
            <h6 className="mb-2">{t("portalArayuzleri.degisenHucreler")}</h6>
            <ul className="small">
              {(onizleme?.degisiklikler ?? []).map((d) => (
                <li key={d.hucre}>
                  {t(d.yabanci ? "portalArayuzleri.yabanci" : "portalArayuzleri.yerli")} {tp(`arayuz.${d.rol}`)} · {t(`portalArayuzleri.modul.${d.modul}`)}:{" "}
                  <SeviyeChip seviye={d.once} /> → <SeviyeChip seviye={d.sonra} />
                </li>
              ))}
            </ul>
            {etki && (
              <>
                <p className="fw-semibold mb-1" data-testid="portal-arayuz-etki-ozet">
                  {etki.kullaniciSayisi === 0
                    ? t("portalArayuzleri.etkiYok")
                    : t("portalArayuzleri.etkiOzet", { firma: etki.firmaSayisi, kullanici: etki.kullaniciSayisi })}
                </p>
                <div className="text-muted small mb-2">{t("portalArayuzleri.etkiAciklama")}</div>
                {etki.kesildi && <Alert color="warning" className="small py-2">{t("portalArayuzleri.etkiKesildi")}</Alert>}
                {(etki.firmalar ?? []).map((f) => (
                  <div key={f.firmaId} className="border rounded p-2 mb-2 small" data-firma={f.firmaId}>
                    <div className="fw-semibold">{f.firmaAdi} <span className="text-muted">#{f.firmaId}</span></div>
                    <div className="text-muted">{t("portalArayuzleri.etkiKullanicilar", { n: f.etkilenenKullaniciSayisi })}</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {f.kullanicilar.map((k) => <Badge color="light" className="text-dark" key={k.userId} data-kullanici={k.loginAdi}>{k.loginAdi}</Badge>)}
                    </div>
                  </div>
                ))}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setOnizleme(null)}>{t("portalArayuzleri.vazgec")}</Button>
            <Button color="primary" disabled={calisiyor} onClick={onayla} data-testid="portal-arayuz-onayla">
              {calisiyor ? <Spinner size="sm" /> : t("portalArayuzleri.onayla")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
